// Type Imports
import type { AuthProvider } from '@refinedev/core'

// API Imports
import { ApiError, API_URL, apiFetch, refreshSession } from '@/lib/api/client'
import { clearAccessToken, getAccessToken, setAccessToken } from '@/lib/api/token-store'
import { safeNext } from '@/lib/safe-next'

import type { SessionUser } from '@/types/api'

/**
 * ! `identifier` — И-МЭЙЛ ЭСВЭЛ АЖИЛТНЫ КОД. Сервер тал өөрөө таньж, зохих
 * ! урсгал руу чиглүүлнэ ([[auth.controller]] `login`). Панел нь аль нь
 * ! болохыг шийдэхгүй: хоёр талд хоёр таних дүрэм байвал зөрөх нь цаг
 * ! хугацааны асуудал.
 */
type LoginParams = {
  identifier: string
  password: string

  /**
   * Нэвтэрсний дараа очих зам — прокси `?next=`-ээр дамжуулна.
   *
   * ! Прокси нь нэвтрээгүй хүнийг `/sign-in?next=/erp/orders` рүү илгээдэг
   * ! атал энэ провайдер `redirectTo: '/'` гэж ХАТУУ бичсэн байв — өөрөөр
   * ! хэлбэл хэрэглэгч дарсан холбоосдоо хэзээ ч хүрдэггүй, үргэлж нүүр
   * ! хуудас руу унадаг байсан.
   */
  next?: string
}

type LoginResponse = {
  accessToken: string
  user: SessionUser
}

/*
 * `check` is the only place that has to work on a cold page load: the access
 * token lives in memory and is gone after a reload, so it asks the API to mint a
 * new one from the httpOnly refresh cookie before deciding the user is signed out.
 */
export const authProvider: AuthProvider = {
  login: async ({ identifier, password, next }: LoginParams) => {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ identifier, password })
      })

      if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as { message?: string | string[] }
        const message = Array.isArray(body.message) ? body.message[0] : body.message

        return {
          success: false,
          error: {
            name: 'LoginError',
            message: message ?? 'Нэвтрэх үед алдаа гарлаа',
            statusCode: response.status
          }
        }
      }

      /*
       * ! `.json()`-ЫГ ШУУД ДУУДАХГҮЙ. Хоосон бие дээр тэр нь
       * ! `Unexpected end of JSON input` гэж унаж, доорх `catch` түүнийг
       * ! «Сервертэй холбогдож чадсангүй» болгон ХУДАЛ тайлагнана —
       * ! сервер холбогдсон, зүгээр л хариу тасарсан байхад.
       */
      const text = await response.text()

      if (text.trim() === '') {
        return {
          success: false,
          error: {
            name: 'EmptyResponse',
            message: 'Сервер хоосон хариу буцаав — дахин оролдоно уу',
            statusCode: response.status
          }
        }
      }

      let payload: LoginResponse

      try {
        payload = JSON.parse(text) as LoginResponse
      } catch {
        return {
          success: false,
          error: { name: 'BadResponse', message: 'Серверийн хариу JSON биш байна', statusCode: response.status }
        }
      }

      if (!payload.accessToken) {
        return {
          success: false,
          error: { name: 'BadResponse', message: 'Хариунд токен байхгүй', statusCode: response.status }
        }
      }

      setAccessToken(payload.accessToken)

      /*
       * ! 🔴 `next` ХАЯГНААС ирдэг — `safeNext` зөвхөн энэ сайтын замыг
       * ! үлдээнэ (`/\evil`, `/%09/evil` зэргийг ч, INJ-3). Refine-ийн
       * ! `?to=` нь үүнийг ТОЙРДОГ тул `SignInView` түүнийг хаягнаас хасна.
       */
      return { success: true, redirectTo: safeNext(next) }
    } catch {
      return {
        success: false,
        error: { name: 'NetworkError', message: 'Сервертэй холбогдож чадсангүй', statusCode: 0 }
      }
    }
  },

  logout: async () => {
    await apiFetch<void>('/auth/logout', { method: 'POST' }).catch(() => undefined)
    clearAccessToken()

    /*
     * ! БҮРЭН ачаалалт (зөөлөн `router.push` БИШ): модулийн түвшний стор (AI чатын урссан хариу), react-query-ийн кэш
     * ! (BI, AI …) нь зөөлөн шилжилтийг давдаг — ижил табд дараагийн хэрэглэгч «Буцах»-аар өмнөхийн мөнгөн дүнг харж
     * ! болно (docs/50 §18, leak-injection-1). `redirectTo`-гүй — Refine дахин шилжүүлэхгүй.
     */
    if (typeof window !== 'undefined') {
      window.location.assign('/sign-in')

      return { success: true }
    }

    return { success: true, redirectTo: '/sign-in' }
  },

  check: async () => {
    if (getAccessToken() || (await refreshSession())) {
      return { authenticated: true }
    }

    return { authenticated: false, logout: true, redirectTo: '/sign-in' }
  },

  onError: async error => {
    if (error instanceof ApiError && error.statusCode === 401) {
      clearAccessToken()

      return { logout: true, redirectTo: '/sign-in', error }
    }

    // A 403 means signed in but not allowed — staying put and rendering the
    // message beats bouncing the user to the sign-in screen they just left.
    return { error }
  },

  getIdentity: async () => {
    try {
      return await apiFetch<SessionUser>('/auth/me')
    } catch {
      return null
    }
  },

  getPermissions: async () => {
    try {
      const me = await apiFetch<SessionUser>('/auth/me')

      return me.permissions
    } catch {
      return []
    }
  }
}
